/**
 * Data layer for `conversion-crud-view-edit-reject-permission-scope`. Thin typed fetch wrappers over /api/v1/conversion-crud-view-edit-reject-permission-scope.
 * Raw `fetch()` is confined to this file per the dash contract.
 */

export type ScopeRole = "advertiser" | "manager" | "affiliate" | "admin";
export type ScopeAction = "view" | "edit" | "reject";

export interface ConversionCrudViewEditRejectPermissionScopeRow {
	id: number;
	tenant_id: string;
	conversion_id: string;
	role: ScopeRole | string;
	action: ScopeAction | string;
	allowed: number;
	reason: string | null;
	created_at: number;
}

export interface ListConversionCrudViewEditRejectPermissionScopeResponse {
	items: ConversionCrudViewEditRejectPermissionScopeRow[];
}

export interface CreateConversionCrudViewEditRejectPermissionScopeBody {
	conversion_id: string;
	role: ScopeRole;
	action: ScopeAction;
	allowed?: boolean | 0 | 1;
	reason?: string | null;
}

export interface CreateConversionCrudViewEditRejectPermissionScopeResponse {
	ok: true;
	conversion_id: string;
	role: string;
	action: string;
	allowed: number;
	created_at: number;
}

export interface CheckParams {
	conversion_id: string;
	role: ScopeRole;
	action: ScopeAction;
}

export interface CheckResponse {
	allowed: boolean;
	reason: string | null;
}

const BASE = "/api/v1/conversion-crud-view-edit-reject-permission-scope";

async function parseOrThrow<T>(res: Response): Promise<T> {
	const text = await res.text();
	let data: unknown = null;
	if (text) {
		try {
			data = JSON.parse(text);
		} catch {
			data = text;
		}
	}
	if (!res.ok) {
		const msg =
			data &&
			typeof data === "object" &&
			data !== null &&
			"error" in data &&
			typeof (data as { error: unknown }).error === "string"
				? (data as { error: string }).error
				: `HTTP ${res.status}`;
		throw new Error(msg);
	}
	return data as T;
}

export interface ListFilters {
	conversion_id?: string;
	role?: ScopeRole | string;
	action?: ScopeAction | string;
}

export async function list(
	filters: ListFilters = {},
): Promise<ListConversionCrudViewEditRejectPermissionScopeResponse> {
	const qs = new URLSearchParams();
	if (filters.conversion_id) qs.set("conversion_id", filters.conversion_id);
	if (filters.role) qs.set("role", filters.role);
	if (filters.action) qs.set("action", filters.action);
	const query = qs.toString();
	const url = query ? `${BASE}?${query}` : BASE;
	const res = await fetch(url, {
		method: "GET",
		credentials: "include",
		headers: { accept: "application/json" },
	});
	return parseOrThrow<ListConversionCrudViewEditRejectPermissionScopeResponse>(res);
}

export async function create(
	body: CreateConversionCrudViewEditRejectPermissionScopeBody,
): Promise<CreateConversionCrudViewEditRejectPermissionScopeResponse> {
	const res = await fetch(BASE, {
		method: "POST",
		credentials: "include",
		headers: {
			accept: "application/json",
			"content-type": "application/json",
		},
		body: JSON.stringify(body),
	});
	return parseOrThrow<CreateConversionCrudViewEditRejectPermissionScopeResponse>(res);
}

export async function check(params: CheckParams): Promise<CheckResponse> {
	const qs = new URLSearchParams({
		conversion_id: params.conversion_id,
		role: params.role,
		action: params.action,
	});
	const res = await fetch(`${BASE}/check?${qs.toString()}`, {
		method: "GET",
		credentials: "include",
		headers: { accept: "application/json" },
	});
	return parseOrThrow<CheckResponse>(res);
}

export const conversionCrudViewEditRejectPermissionScopeQueryKeys = {
	all: ["conversion-crud-view-edit-reject-permission-scope"] as const,
	list: (filters: ListFilters = {}) =>
		[
			...conversionCrudViewEditRejectPermissionScopeQueryKeys.all,
			"list",
			filters,
		] as const,
	check: (params: CheckParams) =>
		[
			...conversionCrudViewEditRejectPermissionScopeQueryKeys.all,
			"check",
			params,
		] as const,
};
